import React, { useState, useEffect } from "react";
import { Trash2, Edit2, PlusCircle, Save, X } from 'lucide-react';
import { base_url } from "../config/config";

const ExcelBulkPage = () => {
  const [excelBulks, setExcelBulks] = useState([]);
  const [formData, setFormData] = useState({
    ANR: "",
    AdditionalText: "",
    Einzelpreis: "",
    totalHours: "",
    costByUser: "",
    profitOrLoss: "",
    invoiceNo: "",
    Menge: "",
    Gesamtpreis: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchExcelBulks();
  }, []);

  const fetchExcelBulks = async () => {
    try {
      const response = await fetch(`${base_url}/v1/getAllExcelBulks`);
      const data = await response.json();
      setExcelBulks(data);
    } catch (error) {
      console.error("Error fetching records:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        // Update an existing record
        const response = await fetch(`${base_url}/v1/updateExcelBulk/${editingId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });
        const updatedRecord = await response.json();
        setExcelBulks((prev) =>
          prev.map((item) => (item._id === editingId ? updatedRecord : item))
        );
        setEditingId(null);
      } else {
        // Create a new record
        const response = await fetch(`${base_url}/v1/createExcelBulk`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });
        const newRecord = await response.json();
        setExcelBulks((prev) => [...prev, newRecord]);
      }
      // Reset the form
      setFormData({
        ANR: "",
        AdditionalText: "",
        Einzelpreis: "",
        totalHours: "",
        costByUser: "",
        profitOrLoss: "",
        invoiceNo: "",
        Menge: "",
        Gesamtpreis: "",
      });
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error saving record:", error);
    }
  };

  const handleEdit = (record) => {
    setEditingId(record._id);
    setFormData({
      ANR: record.ANR,
      AdditionalText: record.AdditionalText,
      Einzelpreis: record.Einzelpreis,
      totalHours: record.totalHours,
      costByUser: record.costByUser,
      profitOrLoss: record.profitOrLoss,
      invoiceNo: record.invoiceNo,
      Menge: record.Menge,
      Gesamtpreis: record.Gesamtpreis,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${base_url}/v1/deleteExcelBulk/${id}`, { method: "DELETE" });
      setExcelBulks((prev) => prev.filter((item) => item._id !== id));
    } catch (error) {
      console.error("Error deleting record:", error);
    }
  };

  const renderFormModal = () => (
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 ${isModalOpen ? 'visible' : 'invisible'}`}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">
            {editingId ? 'Update Record' : 'Create New Record'}
          </h2>
          <button 
            onClick={() => {
              setIsModalOpen(false);
              setEditingId(null);
              setFormData({
                ANR: "", AdditionalText: "", Einzelpreis: "", totalHours: "",
                costByUser: "", profitOrLoss: "", invoiceNo: "", Menge: "", Gesamtpreis: "",
              });
            }} 
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { name: "ANR", type: "text", required: true },
            { name: "AdditionalText", type: "text" },
            { name: "Einzelpreis", type: "number", required: true },
            { name: "totalHours", type: "text" },
            { name: "costByUser", type: "text" },
            { name: "profitOrLoss", type: "text" },
            { name: "invoiceNo", type: "text" },
            { name: "Menge", type: "number" },
            { name: "Gesamtpreis", type: "number" }
          ].map(({ name, type, required = false }) => (
            <div key={name} className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-2">
                {name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </label>
              <input
                type={type}
                name={name}
                value={formData[name]}
                onChange={handleChange}
                required={required}
                className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
              />
            </div>
          ))}
          <div className="col-span-full flex justify-end space-x-4 mt-6">
            <button 
              type="button" 
              onClick={() => setIsModalOpen(false)} 
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Save size={18} className="mr-2" />
              {editingId ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800">Excel Bulk Management</h1>
          <button 
            onClick={() => {
              setIsModalOpen(true);
              setEditingId(null);
            }}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            <PlusCircle size={18} className="mr-2" />
            Add New Record
          </button>
        </div>

        {/* Data Table */}
        <div className="bg-white shadow-lg rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100 border-b">
                <tr>
                  {[
                    "ANR", "Additional Text", "Einzelpreis", "Total Hours", 
                    "Cost By User", "Profit Or Loss", "Invoice No", "Menge", "Gesamtpreis", "Actions"
                  ].map((header) => (
                    <th key={header} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {excelBulks.map((item) => (
                  <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                    {[
                      "ANR", "AdditionalText", "Einzelpreis", "totalHours", 
                      "costByUser", "profitOrLoss", "invoiceNo", "Menge", "Gesamtpreis"
                    ].map((field) => (
                      <td key={field} className="px-4 py-3 text-sm text-gray-700">
                        {item[field]}
                      </td>
                    ))}
                    <td className="px-4 py-3 flex space-x-2">
                      <button
                        onClick={() => handleEdit(item)}
                        className="text-yellow-600 hover:text-yellow-800 transition-colors"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {excelBulks.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No records found. Click "Add New Record" to get started.
              </div>
            )}
          </div>
        </div>
      </div>

      {renderFormModal()}
    </div>
  );
};

export default ExcelBulkPage;